import { useCallback, useEffect, useState } from 'react';
import { useTokens } from '../theme';
import {
  adjustSeats, getSeats, listFundingRequests, listSeatRequests, resolveSeatRequest,
  submitFundingRequest,
} from '../services/institutions';
import {
  Card, SectionTitle, Stat, StatRow, Button, ErrorState, Field,
} from '../uiKit';
import { MetricsSkeleton } from '../Skeleton.jsx';
import { notifyError, notifySuccess } from '../toast.js';

/**
 * Seat counts + adjust purchased seats. `institutionId` scopes it for a
 * super-admin. `onChange` lets a parent refresh sibling panels after a change.
 */
export default function SeatsPanel({
  institutionId, onChange, requestOnly = false, canEdit = true, canResolveRequests = canEdit,
}) {
  const t = useTokens();
  const [seats, setSeats] = useState(null);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [pendingRequest, setPendingRequest] = useState(null);
  const [plan, setPlan] = useState('PRO');
  // Students waiting on a seat. The COUNT is what justifies buying more; the
  // notes are context for the ones who wrote one.
  const [seatRequests, setSeatRequests] = useState({ openCount: 0, requests: [] });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    try {
      const [data, requestData, seatRequestData] = await Promise.all([
        getSeats(institutionId),
        // Same for the funding-request queue: an admin without billing view
        // gets a 403 there, and the seat counts must still render.
        requestOnly
          ? Promise.resolve()
            .then(() => { return listFundingRequests(institutionId); })
            .catch(() => { return null; })
          : null,
        // Never fail the whole panel over the queue: seat counts are the
        // primary job here and must render even if this read errors. Wrapped
        // in a resolved promise so a SYNCHRONOUS throw becomes a rejection
        // too; a bare .catch() would not have caught one.
        Promise.resolve()
          .then(() => { return listSeatRequests(institutionId); })
          .catch(() => { return null; }),
      ]);
      setSeats(data);
      setSeatRequests(seatRequestData || { openCount: 0, requests: [] });
      const pending = requestData?.requests?.find((request) => {
        return request.type === 'SEATS' && request.status === 'PENDING';
      }) || null;
      setPendingRequest(pending);
      setValue(String(pending?.payload?.seatsPurchased ?? data?.seats?.purchased ?? ''));
      setPlan(pending?.payload?.seatPlan || data?.seatPlan || 'PRO');
    } catch (err) {
      setSeats(null);
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
  }, [institutionId, requestOnly]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const n = parseInt(value, 10);
    if (Number.isNaN(n)) { return; }
    setBusy(true);
    try {
      if (requestOnly) {
        await submitFundingRequest({
          type: 'SEATS',
          payload: { seatsPurchased: n, seatPlan: plan },
        }, institutionId);
      } else {
        await adjustSeats(n, institutionId, plan);
      }
      await load();
      if (onChange) { onChange(); }
      notifySuccess(requestOnly ? 'Seat funding request submitted.' : 'Seat allocation updated.');
    } catch (err) {
      notifyError(err?.response?.data?.error
        || (requestOnly ? 'Could not submit the seat funding request.' : 'Could not update the seat allocation.'));
    } finally {
      setBusy(false);
    }
  };

  const closeSeatRequest = async (requestId, status) => {
    setBusy(true);
    try {
      await resolveSeatRequest(requestId, status, institutionId);
      await load();
      notifySuccess(status === 'RESOLVED' ? 'Marked as resolved.' : 'Request dismissed.');
    } catch (err) {
      notifyError(err?.response?.data?.error || 'Could not update the request.');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <Card><SectionTitle>Seats</SectionTitle><MetricsSkeleton count={4} /></Card>;
  }
  if (loadFailed) {
    return (
      <Card>
        <SectionTitle>Seats</SectionTitle>
        <ErrorState message="Couldn't load seats." onRetry={load} />
      </Card>
    );
  }
  if (!seats) { return null; }
  const purchased = seats.seats?.purchased ?? 0;
  return (
    <Card>
      <SectionTitle>Seats</SectionTitle>
      <StatRow>
        <Stat label="Purchased" value={seats.seats?.purchased ?? 0} />
        <Stat label="Used" value={seats.seats?.used ?? 0} />
        <Stat label="Available" value={seats.seats?.available ?? 0} />
        <Stat label="Plan" value={seats.seatPlan || 'N/A'} />
      </StatRow>
      {pendingRequest ? (
        <p className="mt-3 text-sm" role="status" style={{ fontFamily: t.fonts.body, color: t.muted }}>
          Request pending: {pendingRequest.previous?.seatsPurchased ?? purchased} → {pendingRequest.payload?.seatsPurchased} seats
          {' '}on {pendingRequest.payload?.seatPlan || plan}.
          The current allocation stays active until a super admin approves it.
        </p>
      ) : requestOnly ? (
        <p className="mt-3 text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>
          Seat changes are submitted to Coffee Chats for approval before billing or access changes.
        </p>
      ) : null}
      {canEdit ? <form
        className="flex flex-wrap items-end gap-3 mt-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) { save(); }
        }}
      >
        <div className="flex w-[140px] flex-col">
          <Field label="Seats" value={value} onChangeText={setValue} inputMode="numeric" placeholder="Seats" className="block mb-0" />
        </div>
        <label className="flex w-[170px] flex-col text-[13px]" style={{ color: t.muted, fontFamily: t.fonts.body }}>
          <span className="mb-1">Plan</span>
          <select
            value={plan}
            onChange={(event) => { setPlan(event.target.value); }}
            className={`min-h-[44px] rounded-md border px-3 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${t.focusRing}`}
            style={{
              borderColor: t.controlBorder, backgroundColor: t.inputBg, color: t.inputText, fontFamily: t.fonts.body,
            }}
          >
            <option value="PRO">Pro</option>
            <option value="PREMIUM">Premium</option>
            <option value="ENTERPRISE">Enterprise</option>
          </select>
        </label>
        <Button
          label={busy ? 'Submitting…' : (requestOnly ? (pendingRequest ? 'Update request' : 'Request change') : 'Update seats')}
          type="submit"
          disabled={busy}
        />
      </form> : null}

      {seatRequests.requests.length > 0 ? (
        <div className="mt-5 border-t pt-4" style={{ borderColor: t.border }}>
          {seatRequests.openCount > 0 ? (
            <p className="text-sm font-semibold" style={{ fontFamily: t.fonts.body, color: t.heading }}>
              {seatRequests.openCount} student{seatRequests.openCount === 1 ? '' : 's'} waiting on a seat
            </p>
          ) : null}
          <ul className="mt-3 flex flex-col gap-3">
            {seatRequests.requests.map((request) => {
              return (
                <li
                  key={request.id}
                  className="rounded-md border p-3"
                  style={{ borderColor: t.border }}
                >
                  <p className="text-xs font-semibold" style={{ fontFamily: t.fonts.mono, color: t.muted }}>
                    {request.kind === 'PLAN' ? 'Plan or credits request' : 'Seat request'}
                  </p>
                  <p className="text-sm" style={{ fontFamily: t.fonts.body, color: t.heading }}>
                    {request.name || request.email}
                  </p>
                  <p className="text-xs" style={{ fontFamily: t.fonts.mono, color: t.muted }}>
                    {request.email}
                  </p>
                  {request.message ? (
                    <p className="mt-2 text-sm" style={{ fontFamily: t.fonts.body, color: t.muted }}>
                      {request.message}
                    </p>
                  ) : null}
                  {canResolveRequests ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button
                        label="Mark resolved"
                        disabled={busy}
                        onClick={() => { closeSeatRequest(request.id, 'RESOLVED'); }}
                      />
                      <Button
                        label="Dismiss"
                        variant="secondary"
                        disabled={busy}
                        onClick={() => { closeSeatRequest(request.id, 'DISMISSED'); }}
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}
